import { useCallback, useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import axiosInstance from '../../../fetchConfig/axiosConfig';
import logger from '../../../utils/logger';
import { CASE_REVIEW_COLUMNS } from '../../../common/mappings';
import { useUser } from '../../UserContext';

/** Default layouts from Propriety.user_columns keyed by TableName (caseReview, summary, townSummary); the user's rows replace the system rows. */
export const useUserColumnsStore = create((set, get) => ({
  layouts: {},
  systemLayouts: {},
  userLayouts: {},
  loadedFor: undefined,
  fetchLayouts: async (userId) => {
    if (get().loadedFor === userId) return;
    set({ loadedFor: userId });
    try {
      const { data } = await axiosInstance.post('/load-user-columns', { UserId: userId });
      if (get().loadedFor !== userId) return;
      const systemLayouts = {};
      const userLayouts = {};
      data.layouts.forEach((layout) => {
        if (layout.UserID == null) systemLayouts[layout.TableName] = layout;
        else (userLayouts[layout.TableName] ??= []).push(layout);
      });
      const layouts = { ...systemLayouts };
      Object.entries(userLayouts).forEach(([tableName, rows]) => {
        const preferred = rows.find((row) => row.DefaultCol);
        if (preferred) layouts[tableName] = preferred;
      });
      set({ systemLayouts, userLayouts, layouts });
    } catch (error) {
      if (get().loadedFor === userId) set({ loadedFor: undefined });
      logger.error('Error loading user columns:', error);
    }
  },
  saveLayout: async (userId, TableName, ViewName, ColKeys, ColWidths) => {
    await axiosInstance.post('/save-user-columns', { UserId: userId, TableName, ViewName, ColKeys, ColWidths });
    set((state) => {
      const existing = (state.userLayouts[TableName] || []).find((row) => row.ViewName === ViewName);
      const saved = { TableName, ViewName, UserID: userId, ColKeys, ColWidths, DefaultCol: existing?.DefaultCol ? 1 : 0 };
      return {
        userLayouts: { ...state.userLayouts, [TableName]: [...(state.userLayouts[TableName] || []).filter((row) => row.ViewName !== ViewName), saved] },
        layouts: { ...state.layouts, [TableName]: saved },
      };
    });
  },
  setDefaultLayout: async (userId, TableName, ViewName) => {
    await axiosInstance.post('/set-default-user-columns', { UserId: userId, TableName, ViewName });
    set((state) => ({
      userLayouts: {
        ...state.userLayouts,
        [TableName]: (state.userLayouts[TableName] || []).map((row) => ({ ...row, DefaultCol: row.ViewName === ViewName ? 1 : 0 })),
      },
    }));
  },
  deleteLayout: async (userId, TableName, ViewName) => {
    await axiosInstance.post('/delete-user-columns', { UserId: userId, TableName, ViewName });
    set((state) => {
      const remaining = (state.userLayouts[TableName] || []).filter((row) => row.ViewName !== ViewName);
      const layouts = { ...state.layouts };
      const active = layouts[TableName];
      if (active?.UserID && active.ViewName === ViewName) {
        const preferred = remaining.find((row) => row.DefaultCol) || state.systemLayouts[TableName];
        if (preferred) layouts[TableName] = preferred;
        else delete layouts[TableName];
      }
      return { userLayouts: { ...state.userLayouts, [TableName]: remaining }, layouts };
    });
  },
  selectLayout: (TableName, layout) => set((state) => {
    const layouts = { ...state.layouts };
    if (layout) layouts[TableName] = layout;
    else delete layouts[TableName];
    return { layouts };
  }),
}));

/**
 * Column visibility for a grid backed by its saved layout: layout, then `dynamicModel` (run type / row-driven
 * rules), then the user's unsaved toggles. Only the toggles mark the view dirty or get written by `saveView`.
 * `fields`, `defaultModel` (used until layouts load) and `dynamicModel` must be memoized by the caller.
 */
export const useColumnLayout = (table, fields, defaultModel, dynamicModel) => {
  const { user } = useUser();
  const layout = useUserColumnsStore((state) => state.layouts[table]);
  const systemLayout = useUserColumnsStore((state) => state.systemLayouts[table]);
  const userViews = useUserColumnsStore((state) => state.userLayouts[table]);
  const fetchLayouts = useUserColumnsStore((state) => state.fetchLayouts);
  const saveLayout = useUserColumnsStore((state) => state.saveLayout);
  const setDefaultLayout = useUserColumnsStore((state) => state.setDefaultLayout);
  const deleteLayout = useUserColumnsStore((state) => state.deleteLayout);
  const selectLayout = useUserColumnsStore((state) => state.selectLayout);
  const [sessionModel, setSessionModel] = useState({});

  useEffect(() => { fetchLayouts(user?.userId); }, [fetchLayouts, user?.userId]);
  useEffect(() => { setSessionModel((prev) => (Object.keys(prev).length ? {} : prev)); }, [layout]);

  const layoutModel = useMemo(
    () => (layout ? Object.fromEntries(fields.map((field) => [field, layout.ColKeys.includes(field)])) : defaultModel),
    [layout, fields, defaultModel],
  );
  const computedModel = useMemo(() => ({ ...layoutModel, ...dynamicModel }), [layoutModel, dynamicModel]);
  const columnVisibilityModel = useMemo(() => ({ ...computedModel, ...sessionModel }), [computedModel, sessionModel]);

  const onColumnVisibilityModelChange = useCallback((model) => {
    const next = Object.fromEntries(Object.entries(model).filter(([field, visible]) => (computedModel[field] ?? true) !== visible));
    setSessionModel((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      return prevKeys.length === nextKeys.length && nextKeys.every((key) => prev[key] === next[key]) ? prev : next;
    });
  }, [computedModel]);

  // ColWidths holds flex values parallel to ColKeys; null keeps the column's code-defined width.
  const columnWidths = useMemo(() => {
    if (!layout) return;
    const widths = {};
    layout.ColKeys.forEach((field, i) => {
      const flex = layout.ColWidths[i];
      if (flex != null) widths[field] = { flex };
    });
    return widths;
  }, [layout]);

  // Town users cannot save views for now (town portal users also share a single id).
  const canSave = Boolean(user?.userAccess) && !user?.townView;
  const isDirty = canSave && Object.entries(sessionModel).some(([field, visible]) => (layoutModel[field] ?? true) !== visible);

  // Pixel widths become flex by their share of the visible row. The total flex of those columns stays
  // what it was, so columns not on screen (other run types) keep a comparable weight. actions stays null.
  const saveView = useCallback(async (apiRef, viewName) => {
    const savedModel = { ...layoutModel, ...sessionModel };
    const fieldSet = new Set(fields);
    const keep = (field) => fieldSet.has(field) && savedModel[field] !== false;
    const visible = apiRef.current.getVisibleColumns().filter((col) => keep(col.field) && col.field !== 'actions');
    const totalPx = visible.reduce((sum, col) => sum + col.computedWidth, 0);
    const prevSum = visible.reduce((sum, col) => sum + (columnWidths?.[col.field]?.flex || 0), 0);
    const scale = prevSum || visible.length;
    const shownFlex = new Map(visible.map((col) => {
      const flex = totalPx ? Math.round((col.computedWidth / totalPx) * scale * 100) / 100 : null;
      return [col.field, flex || null];
    }));
    const rendered = apiRef.current.getAllColumns().map((col) => col.field).filter(keep);
    const renderedSet = new Set(rendered);
    const ColKeys = [...rendered, ...(layout?.ColKeys ?? []).filter((field) => keep(field) && !renderedSet.has(field))];
    await saveLayout(user.userId, table, viewName, ColKeys, ColKeys.map((field) => shownFlex.get(field) ?? columnWidths?.[field]?.flex ?? null));
  }, [layoutModel, sessionModel, fields, columnWidths, layout, saveLayout, user?.userId, table]);

  const selectView = useCallback((name) => {
    selectLayout(table, name === 'Default' ? systemLayout : userViews?.find((row) => row.ViewName === name));
  }, [selectLayout, table, systemLayout, userViews]);
  const viewName = layout?.UserID ? layout.ViewName : 'Default';
  const setDefault = useCallback(async () => {
    if (viewName === 'Default') return;
    await setDefaultLayout(user.userId, table, viewName);
  }, [setDefaultLayout, user?.userId, table, viewName]);
  const deleteView = useCallback(async (name) => {
    if (!name || name === 'Default') return;
    await deleteLayout(user.userId, table, name);
  }, [deleteLayout, user?.userId, table]);
  const views = useMemo(() => [
    { name: 'Default' },
    ...(userViews || []).filter((row) => row.ViewName !== 'Default').map((row) => ({ name: row.ViewName })),
  ], [userViews]);
  const columnLayout = useMemo(() => ({
    canSave, isDirty, saveView, setDefault, deleteView, selectView, viewName, views,
  }), [canSave, isDirty, saveView, setDefault, deleteView, selectView, viewName, views]);

  return { columnVisibilityModel, onColumnVisibilityModelChange, columnWidths, columnLayout };
};

/** Sale columns that also exist in assessment runs, so a saved caseReview layout must not show them there. */
export const SALE_ONLY_COLUMNS = ['SalePrice', 'SaleDate', 'MLSNumber', 'ALFlag'];

// caseReview visibility until the saved layouts load (mirrors the seeded default row).
export const getDefaultColumnVisibility = (IS_SALE_RUN = false) => {
  const model = {
    // Core columns - always visible
    actions: true,
    PropertyInfo: true,
    PhotoURL: true,
    // SDName: true,
    PCC: true,
    Style: true,
    YearBuilt: true,
    EffYearBuilt: false,
    Sqft: true,
    
    Acres: true,
    // Grade: true,
    // Condition: true,
    IFMV: true,
    Taxes: false,
    Distance: true,
    totalBaths: true,

    Bedrooms: false,
    CentralAir: false,
    View: false, // Hidden by default -- have it dynamically render when relevant
    // Hidden by default columns
    Town: false,
    Fireplaces: false,
    TotalGarageCap: false, // Hidden by default
    EstSqft: false, // Hidden by default
    RegridBldgFtPrnt: false, // Hidden by default
    RegridConvertedSqFt: false, // Hidden by default
    SDName: false,
    SDCode: false,
    ZipCode: false,
    NeighborhoodCode: false,
    parcel_id: false,
    Kitchens: false,
    FinishedBasement: false, // Hidden by default
    Deck: false, // Hidden by default
    Patio: false, // Hidden by default
    Porch: false, // Hidden by default
    fullBaths: false, // Hidden by default
    halfBaths: false, // Hidden by default
    PropertyAssessment: false, // Hidden by default
    ContractDate: false, // Hidden by default
    be_adj_price: false, // Hidden by default
    be_adj_IFMV: false, // Hidden by default; assessment runs only
    gross_adj_pct: false, // Hidden by default
    net_adj_pct: false, // Hidden by default
    Pool: false, // Hidden by default (IG Pool)
    LOA: false, // Hidden by default
    CalculatedTAV: false, // Hidden by default
    PrevAssessment: false,
    Grade: false,
    Condition: false,
    LastDataUpdate: false,

    // Dynamic columns based on run type
    SalePrice: IS_SALE_RUN, // Show SalePrice in sale runs
    SaleDate: IS_SALE_RUN, // Show SaleDate in sale runs
    RecentSale: IS_SALE_RUN, // Show RecentSale (arms length sale) in sale runs
    MLSNumber: IS_SALE_RUN, // Show MLSNumber in sale runs
    ALFlag: IS_SALE_RUN, // Show ALFlag (arms length flag) in sale runs
    adj_IFMV: !IS_SALE_RUN, // Show adj_IFMV in assessment runs
    time_adj: IS_SALE_RUN, // Show time_adj in sale runs
  };
  for (const col of CASE_REVIEW_COLUMNS) {
    if (col.hidden && model[col.field] === undefined) model[col.field] = false;
  }
  return model;
};
